// GOAP — goal-oriented action planning for the ground view's enemies
// (phase D of the AOD Studio arc). Ported from adventure-craft
// `packages/ai/src/goap/` (Planner/Agent/types, 183 lines) under the
// port-safe rule: zero adventure-craft imports, fully deterministic —
// A* over a sorted open list, no `Math.random`, no `Date.now` (the
// original already had both properties; this port keeps them).
//
// Rewritten in the repo's function-first idiom: the agent is a plain
// object, the planner a pure function. The planner is exponential in the
// worst case, so the enemy layer feeds it a *small* action set (4–6
// actions) and a shallow world state (≤ 8 keys).

export type WorldStateValue = boolean | number | string;
/** The agent's belief about the ground view: sparse key → value. */
export type WorldState = Map<string, WorldStateValue>;

export interface GoapGoal {
  /** Debug name ('strike', 'holdPost', …). */
  readonly name: string;
  /** Higher wins when several goals are satisfiable. */
  readonly priority: number;
  /** The state that satisfies the goal (partial — unmatched keys ignored). */
  readonly desiredState: Record<string, WorldStateValue>;
}

export interface GoapAction {
  readonly name: string;
  readonly cost: number;
  readonly preconditions: Record<string, WorldStateValue>;
  readonly effects: Record<string, WorldStateValue>;
  /** Context gate beyond the state preconditions (in range, cooldown ready…). */
  readonly proceduralPrecondition?: (world: GroundViewWorld) => boolean;
  /** How long the action occupies the agent, in seconds. */
  readonly durationSeconds: number;
}

/**
 * The slice of ground-view truth an action may read. Deliberately
 * narrower than the enemy layer's state: actions cannot reach into
 * rendering, and the planner stays testable with a stub.
 */
export interface GroundViewWorld {
  readonly enemy: EnemyFacts;
  readonly player: PlayerFacts;
  readonly nowSeconds: number;
}

export interface EnemyFacts {
  readonly x: number;
  readonly z: number;
  readonly hp: number;
  readonly maxHp: number;
  readonly postX: number;
  readonly postZ: number;
  /** Seconds until the next ability is ready (0 = ready now). */
  readonly abilityCooldownRemaining: number;
  /** The enemy's widest attack envelope (its special's range): inside
   *  this, some ability can reach the player. */
  readonly attackRange: number;
}

export interface PlayerFacts {
  readonly x: number;
  readonly z: number;
  readonly hp: number;
}

/** The agent: beliefs, drives, and the plan currently being walked. */
export interface GoapAgentState {
  readonly world: WorldState;
  readonly goals: readonly GoapGoal[];
  readonly actions: readonly GoapAction[];
  plan: readonly GoapAction[] | null;
  currentAction: GoapAction | null;
  /** Seconds remaining on the current action's occupancy. */
  currentActionRemaining: number;
}

export function createAgent(
  goals: readonly GoapGoal[],
  actions: readonly GoapAction[],
): GoapAgentState {
  return {
    world: new Map(),
    goals,
    actions,
    plan: null,
    currentAction: null,
    currentActionRemaining: 0,
  };
}

/**
 * Plan for the highest-priority goal a plan exists for — the original's
 * `Agent.formulatePlan` shape: goals are tried in priority order and the
 * first goal with a plan wins. (Planning "any goal satisfied" instead
 * would let a cheap low-priority goal steal a tie from the strike.)
 * Within one goal this is uniform-cost search over world states — the
 * original shipped A* with heuristic 0, and these shallow states need
 * no more. Returns null when no goal is reachable.
 */
export function formulatePlan(
  startState: WorldState,
  goals: readonly GoapGoal[],
  actions: readonly GoapAction[],
  world: GroundViewWorld,
): readonly GoapAction[] | null {
  const usable = actions.filter((a) => a.proceduralPrecondition?.(world) ?? true);
  const byPriority = [...goals].sort((a, b) => b.priority - a.priority);
  for (const goal of byPriority) {
    const plan = planForGoal(startState, goal, usable);
    if (plan) return plan;
  }
  return null;
}

function planForGoal(
  startState: WorldState,
  goal: GoapGoal,
  usable: readonly GoapAction[],
): readonly GoapAction[] | null {
  const open: PlanNode[] = [{ parent: null, action: null, state: new Map(startState), cost: 0 }];
  const closed = new Set<string>();

  while (open.length > 0) {
    open.sort((a, b) => a.cost - b.cost);
    const current = open.shift()!;
    if (satisfiesPreconditions(goal.desiredState, current.state)) return buildPath(current);

    const key = serializeState(current.state);
    if (closed.has(key)) continue;
    closed.add(key);

    for (const action of usable) {
      if (!satisfiesPreconditions(action.preconditions, current.state)) continue;
      open.push({
        parent: current,
        action,
        state: applyEffects(current.state, action.effects),
        cost: current.cost + action.cost,
      });
    }
  }
  return null;
}

/**
 * One agent step. Occupancy first (a `strike` winds up over its whole
 * duration), then the plan, re-formulating when it runs dry. Mutates
 * `agent`; returns the action begun this step, if any.
 */
export function agentStep(
  agent: GoapAgentState,
  world: GroundViewWorld,
  dt: number,
): GoapAction | null {
  refreshBeliefs(agent, world);

  if (agent.currentAction) {
    agent.currentActionRemaining -= dt;
    if (agent.currentActionRemaining > 0) return null;
    agent.currentAction = null;
  }

  if (!agent.plan || agent.plan.length === 0) {
    agent.plan = formulatePlan(agent.world, agent.goals, agent.actions, world);
    if (!agent.plan || agent.plan.length === 0) return null;
  }

  const next = agent.plan[0]!;
  if (next.proceduralPrecondition && !next.proceduralPrecondition(world)) {
    agent.plan = null; // world moved out from under the plan — re-plan
    return null;
  }
  agent.plan = agent.plan.slice(1);
  agent.currentAction = next;
  agent.currentActionRemaining = next.durationSeconds;
  return next;
}

/** Read the ground view into beliefs. Called every step, before planning. */
function refreshBeliefs(agent: GoapAgentState, world: GroundViewWorld): void {
  // Subclasses of belief are derived, not stored: the goals and action
  // preconditions speak these keys and the ground view answers them.
  const { enemy, player } = world;
  const dist = Math.hypot(enemy.x - player.x, enemy.z - player.z);
  const postDist = Math.hypot(enemy.x - enemy.postX, enemy.z - enemy.postZ);
  const set = (k: string, v: WorldStateValue) => agent.world.set(k, v);
  set('playerVisible', dist <= SIGHT_RANGE);
  set('playerInReach', dist <= enemy.attackRange);
  set('abilityReady', enemy.abilityCooldownRemaining <= 0);
  set('onPost', postDist <= ON_POST_RANGE);
  set('wounded', enemy.hp < enemy.maxHp * 0.35);
}

/** How far an enemy can see the player (world units ≈ hex widths). */
export const SIGHT_RANGE = 7;
/** How close to the post counts as holding it. */
export const ON_POST_RANGE = 1.2;

function satisfiesPreconditions(pre: Record<string, WorldStateValue>, state: WorldState): boolean {
  for (const [k, v] of Object.entries(pre)) {
    if (state.get(k) !== v) return false;
  }
  return true;
}

function applyEffects(state: WorldState, effects: Record<string, WorldStateValue>): WorldState {
  const next = new Map(state);
  for (const [k, v] of Object.entries(effects)) next.set(k, v);
  return next;
}

/** One search node in the uniform-cost plan. */
interface PlanNode {
  readonly parent: PlanNode | null;
  readonly action: GoapAction | null;
  readonly state: WorldState;
  cost: number;
}

function buildPath(node: PlanNode): readonly GoapAction[] {
  const path: GoapAction[] = [];
  let curr: PlanNode | null = node;
  while (curr?.action) {
    path.unshift(curr.action);
    curr = curr.parent;
  }
  return path;
}

function serializeState(state: WorldState): string {
  return [...state.entries()].sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0)).toString();
}
